/**
 * Every file in the conversation, as one zip (#1069 Story 7.5, FR78; D11,
 * D12, D13, D14).
 *
 * The orchestration of "Save all", as its own hook. The panel asks `start()`;
 * the hook runs the offline check, the client-side ceiling check (AC 15: a
 * refusal the client can already see is made WITHOUT sending the request,
 * `correspondence.md`'s "not a limit, a punishment"), then the transfer on
 * the platform's own transport, then the Android toast and the retry dialog.
 * The panel renders its surface: `saving` and `percent` on the Files row,
 * `failure` on the panel's line.
 *
 * ⚠️ IT IS ITS OWN HOOK, NOT A MEMBER OF `useCorrespondence` (D13, the 7.6
 * D3 precedent). A zip is a transfer with a lifetime that ends on the client,
 * not server state, so it is not a query; and it is not a mutation either --
 * `uploadOverlay`'s lesson is that the library's `status` describes your
 * callback, not the write, and this story has no write. `useJumpToMessage.ts`
 * is the shape: refs for the callbacks, an `alive` ref, a reset on
 * `openThreadId`.
 *
 * ⚠️ `Platform.OS` IS READ IN THE BODY, NEVER AT MODULE SCOPE (7.2 D9). Web
 * fetches the zip as a Blob (`getBlobWithProgress`) and hands it to the
 * browser's own download UI (`saveBlobFile`); native streams it to disk
 * (`downloadToDevice`) and hands the path to the Downloads copy or the share
 * sheet. Bytes never enter JavaScript on native (C9).
 *
 * ⚠️ THE PERCENT IS A MEASUREMENT, NOT AN ANIMATION (D12, C10). A streamed
 * zip is chunked and carries no `Content-Length`, so both transports hand
 * over raw received bytes and the denominator is the listing's own
 * `sizeBytes` sum -- the number the server checks against the ceiling. With
 * `store` (no deflate) the true total exceeds the sum by ~30 bytes plus the
 * name per entry, so the bar is floored and clamped to 99 until the transfer
 * settles, then reads 100 for one render, then `null`. A TRUNCATED listing's
 * sum is only a lower bound, so its percent is `null` throughout and the row
 * says "Saving…" with no bar (4.1's rule: never animate a fake bar).
 *
 * What cancels a save: `cancel()` (the row's own Cancel, `leaveOpenThread`,
 * access revoked, the listing emptying) and an `openThreadId` change. A
 * cancelled zip lands nowhere on any platform and says nothing (D14). Nothing
 * else -- collapsing the strip does not cancel a save; the bar is on the row,
 * and the row stays.
 *
 * @see src/components/correspondence/__tests__/useSaveAllFiles.test.ts
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { buildApiHeaders, getResolvedApiBaseUrl } from '../../services/api';
import {
  downloadToDevice,
  saveBlobFile,
} from '../../services/attachmentService';
import { getBlobWithProgress } from '../../services/correspondence/getWithProgress';
import { isZipTooLarge } from '../../services/correspondence/messageFileLimits';
import {
  zipFileName,
  zipTooLargeReason,
} from '../../services/correspondence/zipFileName';
import { networkService } from '../../services/network';
import { showConfirmation } from '../../utils/confirmation';
import { showToast } from '../../utils/toast';

import type { ThreadFileListing } from './correspondence.types';
import type { ProgressPercent } from '../../services/correspondence/postWithProgress';

/**
 * Matches `AttachmentActions`: two retries before giving up. ONE definition,
 * here; the panel's `downloadAttachmentFile` imports it (Story 7.5, D13).
 */
export const MAX_DOWNLOAD_RETRIES = 2;

/** The Android toast, verbatim from `downloadAttachmentFile` (AC 17). */
export const FILE_SAVED_TOAST = 'File saved to Downloads';

const ROOT = '/v1';

/**
 * The zip's URL, built the way `messageFileUrl.ts` builds the file's: from
 * `getResolvedApiBaseUrl`, no `?space=` (the client's file URLs carry none;
 * the route resolves the space from the thread).
 */
export const threadZipUrl = (threadId: string): string =>
  `${getResolvedApiBaseUrl()}${ROOT}/correspondence/threads/${encodeURIComponent(
    threadId,
  )}/files.zip`;

export interface UseSaveAllFilesInputs {
  openThreadId: string | null;
  requestId: number | string;
  /** The open conversation's title, for the zip's local name (D7). */
  title: string;
  /** The listing the strip already holds; its `sizeBytes` sum is the denominator. */
  threadFiles: ThreadFileListing | null;
}

export interface SaveAllFiles {
  saving: boolean;
  /** `null` = indeterminate (a truncated listing), or nothing in flight. */
  percent: ProgressPercent;
  start: () => void;
  cancel: () => void;
  /** The reason the last save did not land; `null` after an abort. */
  failure: string | null;
  /**
   * Clear the line WITHOUT cancelling a transfer (the strip's collapse, D14).
   * Additive to D13's surface: the panel needs a clear that is not a cancel.
   */
  dismissFailure: () => void;
}

/** What both transports reject with; read by shape so a mock can throw it. */
interface TransferFailure {
  status: number;
  aborted: boolean;
  message: string;
}

const asTransferFailure = (e: unknown): TransferFailure => {
  if (e && typeof e === 'object') {
    const o = e as Partial<TransferFailure>;
    return {
      status: typeof o.status === 'number' ? o.status : 0,
      aborted: o.aborted === true,
      message:
        typeof o.message === 'string' && o.message
          ? o.message
          : 'Failed to download attachment',
    };
  }
  return {
    status: 0,
    aborted: false,
    message: 'Failed to download attachment',
  };
};

/**
 * D12: `received / expected`, floored, clamped to `[0, 99]` while in flight.
 * `expectedBytes <= 0` has no honest percent (review P8): the caller treats
 * it as indeterminate and never asks; here it is `0`, the floor, so a
 * caller that does ask cannot divide by it.
 */
export const progressPercent = (
  received: number,
  expectedBytes: number,
): number => {
  if (expectedBytes <= 0) return 0;
  const floored = Math.floor((received / expectedBytes) * 100);
  return Math.max(0, Math.min(99, floored));
};

/**
 * The denominator (D12): the listing's own `sizeBytes` sum. `sizeBytes` is
 * `number` on `MessageFile` and `file_size_bytes` is `INT NOT NULL`
 * (migration 144), so the `?? 0` is for a listing built by hand (a stale
 * mock, an older cached shape), never for the wire. A sum of zero is not a
 * denominator: `null`, indeterminate, "Saving…" (review P8). Before this a
 * listing whose sizes were all missing or zero sat at "Saving 0%" for the
 * whole transfer.
 */
export const expectedBytesOf = (listing: ThreadFileListing): number | null => {
  if (listing.truncated === true) return null;
  const sum = listing.items.reduce(
    (total, item) => total + (item.sizeBytes ?? 0),
    0,
  );
  return sum > 0 ? sum : null;
};

export function useSaveAllFiles({
  openThreadId,
  requestId,
  title,
  threadFiles,
}: UseSaveAllFilesInputs): SaveAllFiles {
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState<ProgressPercent>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /** The transfer in flight, so `cancel()` can call it off. */
  const handle = useRef<{ abort: () => void } | null>(null);
  /** Which start an in-flight settlement belongs to; a stale one is ignored. */
  const token = useRef(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  // Set in the body as well as cleared in the cleanup: under StrictMode's
  // simulated unmount/remount the cleanup runs once without a real unmount
  // (the `useJumpToMessage` shape).
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      // No state is set after unmount; a transfer still on the wire is
      // called off, since nothing can hand its result over any more.
      token.current += 1;
      handle.current?.abort();
      handle.current = null;
      inFlight.current = false;
    };
  }, []);

  // The inputs through refs, so the panel may pass what it likes without
  // re-arming anything; `start` reads them at the moment it is pressed.
  const inputs = useRef({ openThreadId, requestId, title, threadFiles });
  inputs.current = { openThreadId, requestId, title, threadFiles };

  const cancel = useCallback(() => {
    token.current += 1;
    const current = handle.current;
    handle.current = null;
    inFlight.current = false;
    current?.abort();
    // An abort says nothing (D14): the row returns to rest, the line is
    // untouched by it -- and a failure from a PREVIOUS save is cleared with
    // the transfer it belonged to, since every caller of cancel is leaving.
    setSaving(false);
    setPercent(null);
    setFailure(null);
  }, []);

  const dismissFailure = useCallback(() => {
    setFailure(null);
  }, []);

  // A thread switch resets everything: the transfer and its line belonged
  // to the conversation that was open.
  useEffect(() => {
    cancel();
  }, [openThreadId, cancel]);

  // D12: 100 for exactly one committed render after the transfer settles,
  // then null.
  useEffect(() => {
    if (!saving && percent === 100) setPercent(null);
  }, [saving, percent]);

  const start = useCallback(() => {
    if (inFlight.current) return;
    const { openThreadId: threadId, threadFiles: listing } = inputs.current;
    if (threadId === null || listing === null) return;

    setFailure(null);

    // ⚠️ OFFLINE RETURNS SILENTLY, WITH NO ALERT AND NO LINE (#1069 offline
    // state, 2026-09-25). The panel HIDES Save all while the device is
    // offline, so this is unreachable from the UI and stays only as a
    // defensive guard. It used to print "Download is unavailable while
    // offline" (web) or raise a native `Alert` -- saying offline through a
    // failure, which is exactly what that change retired. The Android toast
    // and the retry dialog (D13) are unchanged below.
    if (!networkService.isConnected()) return;

    const truncated = listing.truncated === true;
    // `null` when there is no honest denominator: a truncated listing (D12)
    // or a sum of zero (P8). Every percent below is `null` then.
    const expectedBytes = expectedBytesOf(listing);
    // AC 15: what the server would refuse with a 413, refused here first,
    // through the SAME predicate the parity test holds against the server's.
    // A truncated listing's sum is a lower bound and could be under the
    // ceiling while the real total is over it; the server decides then.
    if (expectedBytes !== null && isZipTooLarge(expectedBytes)) {
      setFailure(zipTooLargeReason(expectedBytes));
      return;
    }

    token.current += 1;
    const issued = token.current;
    inFlight.current = true;
    setSaving(true);
    setPercent(expectedBytes === null ? null : 0);

    const isCurrent = () => alive.current && issued === token.current;
    const onProgress = (received: number) => {
      if (!isCurrent() || expectedBytes === null) return;
      setPercent(progressPercent(received, expectedBytes));
    };

    const platform = Platform.OS;
    const url = threadZipUrl(threadId);
    // Story 7.7, D5: the thread id is the fallback for a title that slugs
    // to nothing (Cyrillic, CJK), so two such conversations never share a
    // name on the device.
    const fileName = zipFileName(
      inputs.current.requestId,
      inputs.current.title,
      threadId,
    );

    const transfer = async (): Promise<void> => {
      const headers = await buildApiHeaders();
      if (!isCurrent()) return;
      if (platform === 'web') {
        const web = getBlobWithProgress({
          url,
          headers,
          onProgress,
          // A 200 that is not a zip (an SSO login page) is a failure, not a
          // file named `.zip` full of HTML (P10).
          expectedContentType: 'application/zip',
        });
        handle.current = web;
        const blob = await web.promise;
        if (!isCurrent()) return;
        saveBlobFile(blob, fileName);
        return;
      }
      const native = downloadToDevice({
        url,
        headers,
        fileName,
        mimeType: 'application/zip',
        onProgress,
      });
      handle.current = native;
      await native.promise;
      if (!isCurrent()) return;
      if (platform === 'android') {
        // Web shows its own download UI and iOS shows the share sheet;
        // Android is the one platform with no confirmation of its own.
        showToast.success(FILE_SAVED_TOAST);
      }
    };

    const settle = (fn?: () => void) => {
      if (!isCurrent()) return;
      handle.current = null;
      inFlight.current = false;
      setSaving(false);
      fn?.();
    };

    const run = async (retry: number): Promise<void> => {
      try {
        await transfer();
        settle(() => setPercent(expectedBytes === null ? null : 100));
      } catch (e) {
        const reason = asTransferFailure(e);
        if (!isCurrent()) return;
        if (reason.aborted) {
          // The user's own act, or the panel's: nothing said (D14).
          settle(() => setPercent(null));
          return;
        }
        // A failure that lands while the device is offline is the connection,
        // not the zip: no 'Download Failed … Retry' dialog and no line
        // (#1069 offline state). The row is hidden offline anyway.
        if (!networkService.isConnected()) {
          settle(() => setPercent(null));
          return;
        }
        // A 413 cannot succeed on retry: the set is over the ceiling and
        // retrying it would only ask the same question (AC 14). A 429 is the
        // same shape (Story 7.7, D7): the caller's own zip is still in flight
        // (the guard) or the minute's cap is spent (the limiter), and both
        // sentences already say what to do; an immediate retry asks the same
        // guard or the same window. The reason lands on the line, no dialog.
        if (
          reason.status !== 413 &&
          reason.status !== 429 &&
          retry < MAX_DOWNLOAD_RETRIES
        ) {
          const again = await showConfirmation(
            'Download Failed',
            reason.message,
            'Retry',
            'Cancel',
          );
          if (!isCurrent()) return;
          if (again) {
            setPercent(expectedBytes === null ? null : 0);
            await run(retry + 1);
            return;
          }
        }
        settle(() => {
          setPercent(null);
          setFailure(reason.message);
        });
      }
    };

    void run(0);
  }, []);

  return { saving, percent, start, cancel, failure, dismissFailure };
}
